import type { CannedTallyOption } from '~/components/common/productLengthChoice';
import type { RandomBundlesByProductId } from '~/components/common/tallyBundleCounts';
import {
  buildTallyBundlesByFt,
  computeTallyCount,
  getItemWholeFt,
  getRandomTallyBundleCount,
  ROUNDING_EPSILON,
} from '~/utils/order';
import type { CartLine } from './CartCatalogAddRow';

export function savedCannedTallyCount(
  cartLines: CartLine[],
  cannedTally: CannedTallyOption
): number | undefined {
  const tallyLines = cartLines.filter(
    (line) => !!line.cannedTally && line.cannedTally.id === cannedTally.id
  );
  if (tallyLines.length === 0) return undefined;
  const bundlesByFt = buildTallyBundlesByFt(cannedTally.bundles);
  const coveredLines = tallyLines.filter((line) => {
    const wholeFt = getItemWholeFt(line);
    return wholeFt !== null && (bundlesByFt[wholeFt] ?? 0) > 0;
  });
  return computeTallyCount(coveredLines, bundlesByFt);
}

type SavedRandomTallyBundle = {
  platformProductId: string;
  lengthId: string;
  bundleCount: number;
};

function savedRandomTallyBundleCounts(
  cartLines: CartLine[]
): SavedRandomTallyBundle[] {
  return cartLines.flatMap((line) => {
    const lengthId = line.platformProductsLengths.length?.id;
    if (!line.tallyPlatformProduct || line.cannedTally || !lengthId) return [];
    const bundleCount = getRandomTallyBundleCount(line);
    if (bundleCount === null) return [];
    return [
      {
        platformProductId: line.tallyPlatformProduct.id,
        lengthId,
        bundleCount,
      },
    ];
  });
}

function isWholeBundleCount(bundleCount: number): boolean {
  return Math.abs(bundleCount - Math.round(bundleCount)) < ROUNDING_EPSILON;
}

export function savedRandomTallyBundles(
  cartLines: CartLine[]
): RandomBundlesByProductId {
  const bundlesByProduct: Record<string, Record<string, number>> = {};
  for (const bundle of savedRandomTallyBundleCounts(cartLines)) {
    const wholeBundles = Math.ceil(bundle.bundleCount - ROUNDING_EPSILON);
    if (wholeBundles <= 0) continue;
    const productBundles = (bundlesByProduct[bundle.platformProductId] ??= {});
    productBundles[bundle.lengthId] = wholeBundles;
  }
  return bundlesByProduct;
}

export function hasPartialSavedRandomBundles(
  cartLines: CartLine[],
  platformProductId: string
): boolean {
  return savedRandomTallyBundleCounts(cartLines).some(
    (bundle) =>
      bundle.platformProductId === platformProductId &&
      !isWholeBundleCount(bundle.bundleCount)
  );
}
